import { z } from 'zod';
import { expandPermissions } from '../permissions/catalogue.js';
import { isTimeZone } from '../time-zone.js';
import { INTEREST_VALUES, MARKET_CODES, ROLE_VALUES } from '../vocabulary.js';

/**
 * Schema of config/portal.json (brief section 12): business lists and rules,
 * never secrets. The API and the worker refuse to start when the file does not
 * match. Required fields are not here: they are code (validation/fields.ts).
 */

const label = z.string().trim().min(1).max(80);
const positiveInt = z.int().positive();

/** Option lists whose values are fixed by the data model; only labels are configurable. */
function optionList<const V extends readonly [string, ...string[]]>(values: V) {
  return z
    .array(z.strictObject({ value: z.enum(values), label }))
    .refine(
      (options) =>
        options.length === values.length && values.every((v) => options.some((o) => o.value === v)),
      { error: `must list exactly these values: ${values.join(', ')}` },
    );
}

const roleKey = z.string().regex(/^[a-z][a-z0-9_]*$/, {
  error: 'role keys use lowercase letters, digits and underscores',
});

const role = z.strictObject({
  label,
  permissions: z
    .array(z.string())
    .min(1)
    .superRefine((entries, ctx) => {
      for (const entry of expandPermissions(entries).unknown) {
        ctx.addIssue({ code: 'custom', message: `unknown permission "${entry}"` });
      }
    }),
});

const rateLimit = z.strictObject({ max: positiveInt, windowMinutes: positiveInt });

/** Sign-in rules (brief section 9): email and password, then an authenticator app code. */
const auth = z.strictObject({
  /** Role given to INITIAL_ADMIN_EMAIL when the portal has no administrator yet. */
  initialAdminRole: roleKey,
  password: z.strictObject({
    minLength: z.int().min(12).max(64),
    maxLength: z.int().min(64).max(1024),
  }),
  lockout: z.strictObject({
    maxFailedAttempts: positiveInt.max(20),
    lockoutMinutes: positiveInt,
  }),
  /** Time allowed to enter the authenticator code after the password. */
  codeStepMinutes: positiveInt.max(30),
  /** Time allowed to set up the authenticator app after the password. */
  setupStepMinutes: positiveInt.max(120),
  invitationValidHours: positiveInt.max(336),
  passwordResetValidMinutes: positiveInt.max(1440),
  recoveryCodeCount: z.int().min(5).max(20),
  rateLimits: z.strictObject({
    /** Sign-in and code attempts per network address. */
    signIn: rateLimit,
    /** Password reset requests per network address. */
    passwordReset: rateLimit,
    /** Nov 3, 2026: cards read at Confirm, per person (each is an AI reading). */
    cardReading: rateLimit.default({ max: 120, windowMinutes: 15 }),
  }),
});

export const portalConfigSchema = z
  .strictObject({
    $schema: z.string().optional(),
    version: z.literal(1),
    lists: z
      .strictObject({
        markets: z
          .array(z.strictObject({ code: z.enum(MARKET_CODES), name: label }))
          .refine(
            (markets) =>
              markets.length === MARKET_CODES.length &&
              MARKET_CODES.every((code, i) => markets[i]?.code === code),
            { error: `must list exactly ${MARKET_CODES.join(', ')}, in that order` },
          ),
        countries: z
          .array(z.string().trim().min(2).max(80))
          .min(1)
          .refine((list) => new Set(list).size === list.length, {
            error: 'countries must be unique',
          })
          .refine((list) => list.every((c) => !/^[A-Z]{2,3}$/.test(c)), {
            error: 'countries are full English names, never codes',
          }),
        interestOptions: optionList(INTEREST_VALUES),
        roleOptions: optionList(ROLE_VALUES),
        /**
         * Deal owners offered when HubSpot cannot be reached (step 3). Names
         * only for now; the HubSpot owner of each is matched at step 4.
         */
        dealOwnersFallback: z
          .array(z.strictObject({ name: label }))
          .min(1)
          .refine((list) => new Set(list.map((o) => o.name)).size === list.length, {
            error: 'deal owners must be unique',
          }),
        /**
         * Deal stages of the Sales Pipeline (Lead Entry's list), offered until
         * the stages are read from HubSpot (step 4), and the stage a lead takes
         * when it is qualified (MQLs) or disqualified (Marketing nurturing).
         */
        dealStagesFallback: z
          .array(z.strictObject({ id: z.string().regex(/^[a-z0-9_]{1,64}$/), label }))
          .min(1)
          .refine((list) => new Set(list.map((s) => s.id)).size === list.length, {
            error: 'deal stages must be unique',
          }),
        dealStageDefaults: z.strictObject({ qualified: z.string(), disqualified: z.string() }),
        /**
         * Expenses (step 8): the budget lines a receipt is assigned to
         * (provisional until accounting gives its list), and the currencies
         * offered first (ISO codes; the reading may give another one).
         */
        budgetLines: z
          .array(label)
          .min(1)
          .refine((list) => new Set(list).size === list.length, {
            error: 'budget lines must be unique',
          }),
        expenseCurrencies: z.array(z.string().regex(/^[A-Z]{3}$/)).min(1),
        /** Content (step 6): the languages of the library's videos and PDFs (ISO codes). */
        contentLanguages: z
          .array(z.strictObject({ id: z.string().regex(/^[a-z]{2}$/), label }))
          .min(1)
          .refine((list) => new Set(list.map((l) => l.id)).size === list.length, {
            error: 'content languages must be unique',
          }),
      })
      .refine(
        (lists) =>
          [lists.dealStageDefaults.qualified, lists.dealStageDefaults.disqualified].every((id) =>
            lists.dealStagesFallback.some((s) => s.id === id),
          ),
        { error: 'dealStageDefaults must name stages of dealStagesFallback' },
      ),
    retention: z.strictObject({
      contactsLeadsAfterWrapupDays: positiveInt,
      officeRecordsAfterPushDays: positiveInt,
      expensesVisibleDays: positiveInt,
      roiTrashDays: positiveInt,
      packagesAfterExpiryDays: positiveInt,
      travelAfterEventDays: positiveInt,
      auditLogMonths: positiveInt,
      /** Round 2: a record deleted in the portal or in HubSpot leaves for good after these days. */
      deletedRecordsDays: positiveInt,
    }),
    defaults: z.strictObject({
      pageSize: positiveInt.max(100),
      linkPickerSize: positiveInt.max(50),
      kioskIdleSeconds: positiveInt,
      /** Used in emails when the person's own time zone is not known yet. */
      timeZone: z.string().refine(isTimeZone, { error: 'must be an IANA time zone' }),
    }),
    kioskPin: z.strictObject({
      digits: z.int().min(6).max(12),
      maxFailedAttempts: positiveInt.max(20),
      lockoutMinutes: positiveInt,
    }),
    offline: z.strictObject({
      sessionDays: positiveInt.max(30),
      /** Round 2: from this many days before a show, its card and Travel remind to prepare offline content. */
      prepareDaysBefore: positiveInt.max(90),
    }),
    /**
     * Visitor forms (Hand the tablet, booth kiosk) and prospect pages: the
     * privacy policy's address, empty until it is published (no link shown
     * until then), and the version of the consent sentence stored with each
     * consent (brief sections 3, 6.4 and 12). A new sentence gets a new version.
     */
    privacy: z.strictObject({
      policyUrl: z.union([z.literal(''), z.url({ protocol: /^https$/ })]),
      consentVersion: z.string().trim().min(1).max(50),
    }),
    roles: z.record(roleKey, role).refine((roles) => Object.keys(roles).length > 0, {
      error: 'at least one role is required',
    }),
    auth,
  })
  .superRefine((config, ctx) => {
    const admin = config.roles[config.auth.initialAdminRole];
    if (!admin || !expandPermissions(admin.permissions).permissions.has('admin.users.manage')) {
      ctx.addIssue({
        code: 'custom',
        path: ['auth', 'initialAdminRole'],
        message: 'must be a role that grants admin.users.manage',
      });
    }
  });

export type PortalConfig = z.infer<typeof portalConfigSchema>;

export class PortalConfigError extends Error {
  constructor(
    message: string,
    readonly problems: string[],
  ) {
    super(`${message}\n${problems.map((p) => `  - ${p}`).join('\n')}`);
    this.name = 'PortalConfigError';
  }
}

/** Validates already-parsed JSON. Throws PortalConfigError listing every problem. */
export function parsePortalConfig(data: unknown): PortalConfig {
  const result = portalConfigSchema.safeParse(data);
  if (!result.success) {
    const problems = result.error.issues.map(
      (issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`,
    );
    throw new PortalConfigError('config/portal.json is invalid:', problems);
  }
  return result.data;
}
